/**
 * Full-Width-F Offset Utility
 *
 * Calculates the distance from the left edge of the browser viewport to the
 * left edge of the `.full-width-f` container and exposes it as the CSS custom
 * property `--full-width-f-offset` on `:root` so SCSS/blocks can consume it.
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

/**
 * Measure and publish horizontal offset for `.full-width-f` based on the
 * original container width (not on the shifted element position).
 * Falls back to 0px when the element is absent.
 */
function updateFullWidthFOffset() {
	const el = document.querySelector( '.full-width-f' );

	if ( ! el ) {
		document.documentElement.style.setProperty( '--full-width-f-offset', '0px' );
		return;
	}

	const container = el.parentElement;
	const containerWidth = container
		? container.getBoundingClientRect().width
		: el.getBoundingClientRect().width;
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
	const offset = Math.max( ( viewportWidth - containerWidth ) / 2, 0 );

	document.documentElement.style.setProperty( '--full-width-f-offset', `${ offset }px` );
}

/**
 * Schedule offset calculation after layout/paint.
 */
function scheduleFullWidthFOffsetUpdate() {
	window.requestAnimationFrame( () => {
		window.requestAnimationFrame( updateFullWidthFOffset );
	} );
}

/**
 * Create a debounced wrapper.
 *
 * @param {Function} fn Function to debounce.
 * @param {number} wait Delay in milliseconds.
 * @returns {Function} Debounced function.
 */
function debounce( fn, wait ) {
	let timeoutId;

	return function debounced( ...args ) {
		window.clearTimeout( timeoutId );
		timeoutId = window.setTimeout( () => fn.apply( this, args ), wait );
	};
}

/**
 * Initialise the offset tracker.
 * Called once by the theme entry point; attaches its own resize listener.
 */
export function initFullWidthFOffset() {
	const debouncedUpdate = debounce( scheduleFullWidthFOffsetUpdate, 120 );

	scheduleFullWidthFOffsetUpdate();
	window.addEventListener( 'resize', debouncedUpdate );
	window.addEventListener( 'orientationchange', debouncedUpdate );
	window.addEventListener( 'load', scheduleFullWidthFOffsetUpdate );

	// Recalculate after async layout shifts (fonts/content) that can alter offset.
	window.setTimeout( scheduleFullWidthFOffsetUpdate, 60 );
	window.setTimeout( scheduleFullWidthFOffsetUpdate, 240 );

	if ( document.fonts && document.fonts.ready ) {
		document.fonts.ready.then( scheduleFullWidthFOffsetUpdate );
	}
}
